import React from "react";
import { ProgressBar } from "@progress/kendo-react-progressbars";
import { StatusEnumSchema } from "@domain/schemas/BI";
import { Chip } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { warningTriangleIcon } from "@progress/kendo-svg-icons";
import { GRID_PREVENT_SELECTION_ELEMENT } from "@progress/kendo-react-grid";

// Custom display labels for statuses whose enum value is not user-friendly.
const STATUS_LABELS: Partial<Record<StatusEnumSchema, string>> = {
  [StatusEnumSchema.SyncPending]: "Syncing image to registry\u{2026}",
};

interface StatusCellProperties {
  status: string;
  isPauseRequested?: boolean;
  progress: number;
  isbatch: boolean;
  currentStep?: string;
  failedBatchesCount?: number;
  /**
   * Renders a warning triangle instead of the "N Failed" chip, for any status rather than only
   * Completed. Used at job level so a job whose runs partially failed is flagged while it is still
   * Running, or once it has Failed outright.
   */
  showFailureWarning?: boolean;
  onFailureClick?: () => void;
  tdProps?: React.TdHTMLAttributes<HTMLTableCellElement>;
  selectionChange?: (event: { syntheticEvent: React.SyntheticEvent<HTMLElement> }) => void;
}

const StatusCell: React.FC<StatusCellProperties> = ({
  status,
  isPauseRequested = false,
  progress,
  isbatch,
  currentStep,
  failedBatchesCount,
  showFailureWarning = false,
  onFailureClick,
  tdProps,
  selectionChange,
}) => {
  const getChipThemeColor = () => {
    switch (status) {
      case StatusEnumSchema.Failed:
      case StatusEnumSchema.Aborted:
      case StatusEnumSchema.Paused: {
        return "error";
      }
      case StatusEnumSchema.Running: {
        return "info";
      }
      case StatusEnumSchema.Completed: {
        return "success";
      }
      case StatusEnumSchema.Created: {
        return "base";
      }
      case StatusEnumSchema.Pending:
      case StatusEnumSchema.SyncPending: {
        return "warning";
      }
      default: {
        return "base";
      }
    }
  };

  const getStatusLabel = () => STATUS_LABELS[status as StatusEnumSchema] ?? status;

  const isShowProgress = status === StatusEnumSchema.Running || status === StatusEnumSchema.SyncPending || (status === StatusEnumSchema.Pending && !isbatch);

  // Use > 0 to ensure the number 0 never renders as text.
  const hasFailures = typeof failedBatchesCount === "number" && failedBatchesCount > 0;
  const failureTitle = `${failedBatchesCount} ${failedBatchesCount === 1 ? "run" : "runs"} failed`;
  const isFailureWarningInteractive = hasFailures && showFailureWarning && onFailureClick;

  return (
    <td
      {...tdProps}
      style={{ ...tdProps?.style, padding: "4px 8px" }}
      onClick={(event) => {
        if (tdProps?.onClick) {
          tdProps.onClick(event);
        } else {
          selectionChange?.({ syntheticEvent: event });
        }
      }}
    >
      {/* Row 1: Status Chip, Failures, and Step Name */}
      <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "nowrap", minWidth: "160px", marginTop: "5px", pointerEvents: "none" }}>
        <Chip style={{ pointerEvents: "none", flexShrink: 0 }} themeColor={getChipThemeColor()} selected={false}>
          {getStatusLabel()}
        </Chip>

        {isPauseRequested && !isbatch && (
          <Chip size="small" themeColor="warning" fillMode="outline" style={{ pointerEvents: "none", flexShrink: 0, borderRadius: "6px" }}>
            Pause Requested
          </Chip>
        )}

        {hasFailures &&
          (showFailureWarning ? (
            isFailureWarningInteractive ? (
              <button
                type="button"
                title={failureTitle}
                aria-label={failureTitle}
                {...{ [GRID_PREVENT_SELECTION_ELEMENT]: true }}
                onMouseUp={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  onFailureClick();
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  if (event.detail === 0) {
                    onFailureClick();
                  }
                }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  flexShrink: 0,
                  color: "#f0ad4e",
                  pointerEvents: "auto",
                  background: "transparent",
                  border: 0,
                  padding: 0,
                  cursor: "pointer",
                }}
              >
                <SvgIcon icon={warningTriangleIcon} />
                <span style={{ fontSize: "12px" }}>{failedBatchesCount}</span>
              </button>
            ) : (
              <span
                title={failureTitle}
                aria-label={failureTitle}
                role="img"
                style={{ display: "inline-flex", alignItems: "center", gap: "4px", flexShrink: 0, color: "#f0ad4e", pointerEvents: "none" }}
              >
                <SvgIcon icon={warningTriangleIcon} />
                <span style={{ fontSize: "12px" }}>{failedBatchesCount}</span>
              </span>
            )
          ) : (
            status === StatusEnumSchema.Completed && (
              <span title={`${failedBatchesCount} batches failed`}>
                <Chip size="small" themeColor="warning" fillMode="outline" style={{ pointerEvents: "none", flexShrink: 0, borderRadius: "6px" }}>
                  {failedBatchesCount} Failed
                </Chip>
              </span>
            )
          ))}

        <span
          style={{
            fontSize: "12px",
            color: "#666",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            paddingLeft: "5px",
            paddingTop: "5px",
            flexShrink: 1,
            visibility: currentStep && isShowProgress ? "visible" : "hidden",
          }}
        >
          {currentStep || ""}
        </span>
      </div>

      {/* Row 2: Progress bar and percentage */}
      <div
        className="custom-progress-bar"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "1px",
          width: "100%",
          marginTop: "2px",
          visibility: isShowProgress ? "visible" : "hidden",
          pointerEvents: "none",
        }}
      >
        <ProgressBar value={progress} style={{ height: "5px", flex: 1 }} />
        <span style={{ fontSize: "12px", color: "#666", minWidth: "35px", textAlign: "right", paddingRight: "5px" }}>{Math.round(progress)}%</span>
      </div>
    </td>
  );
};

export default StatusCell;
